/**
 * Category image manifest. Maps image filename slug (matches the basename of
 * the legacy splash-assets/parts-categories/*.jpg files) to optimized WebP
 * variants hosted on the Lovable CDN, with a same-origin /public fallback.
 *
 * Add/regenerate variants via scripts/resize-cats.mjs + lovable-assets CLI.
 */
import accessoriesCard from "@/assets/parts-categories/accessories-card.webp.asset.json";
import accessoriesThumb from "@/assets/parts-categories/accessories-thumb.webp.asset.json";
import accessoriesHero from "@/assets/parts-categories/accessories-hero.webp.asset.json";
import appliancesCard from "@/assets/parts-categories/appliances-card.webp.asset.json";
import appliancesThumb from "@/assets/parts-categories/appliances-thumb.webp.asset.json";
import appliancesHero from "@/assets/parts-categories/appliances-hero.webp.asset.json";
import electricalCard from "@/assets/parts-categories/electrical-card.webp.asset.json";
import electricalThumb from "@/assets/parts-categories/electrical-thumb.webp.asset.json";
import electricalHero from "@/assets/parts-categories/electrical-hero.webp.asset.json";
import engineCard from "@/assets/parts-categories/engine-card.webp.asset.json";
import engineThumb from "@/assets/parts-categories/engine-thumb.webp.asset.json";
import engineHero from "@/assets/parts-categories/engine-hero.webp.asset.json";
import exteriorCard from "@/assets/parts-categories/exterior-card.webp.asset.json";
import exteriorThumb from "@/assets/parts-categories/exterior-thumb.webp.asset.json";
import exteriorHero from "@/assets/parts-categories/exterior-hero.webp.asset.json";
import hardwareCard from "@/assets/parts-categories/hardware-card.webp.asset.json";
import hardwareThumb from "@/assets/parts-categories/hardware-thumb.webp.asset.json";
import hardwareHero from "@/assets/parts-categories/hardware-hero.webp.asset.json";
import hvacCard from "@/assets/parts-categories/hvac-card.webp.asset.json";
import hvacThumb from "@/assets/parts-categories/hvac-thumb.webp.asset.json";
import hvacHero from "@/assets/parts-categories/hvac-hero.webp.asset.json";
import interiorCard from "@/assets/parts-categories/interior-card.webp.asset.json";
import interiorThumb from "@/assets/parts-categories/interior-thumb.webp.asset.json";
import interiorHero from "@/assets/parts-categories/interior-hero.webp.asset.json";
import lightingCard from "@/assets/parts-categories/lighting-card.webp.asset.json";
import lightingThumb from "@/assets/parts-categories/lighting-thumb.webp.asset.json";
import lightingHero from "@/assets/parts-categories/lighting-hero.webp.asset.json";
import lpgasCard from "@/assets/parts-categories/lpgas-card.webp.asset.json";
import lpgasThumb from "@/assets/parts-categories/lpgas-thumb.webp.asset.json";
import lpgasHero from "@/assets/parts-categories/lpgas-hero.webp.asset.json";
import marineCard from "@/assets/parts-categories/marine-card.webp.asset.json";
import marineThumb from "@/assets/parts-categories/marine-thumb.webp.asset.json";
import marineHero from "@/assets/parts-categories/marine-hero.webp.asset.json";
import plumbingCard from "@/assets/parts-categories/plumbing-card.webp.asset.json";
import plumbingThumb from "@/assets/parts-categories/plumbing-thumb.webp.asset.json";
import plumbingHero from "@/assets/parts-categories/plumbing-hero.webp.asset.json";
import suspensionCard from "@/assets/parts-categories/suspension-card.webp.asset.json";
import suspensionThumb from "@/assets/parts-categories/suspension-thumb.webp.asset.json";
import suspensionHero from "@/assets/parts-categories/suspension-hero.webp.asset.json";
import toolsCard from "@/assets/parts-categories/tools-card.webp.asset.json";
import toolsThumb from "@/assets/parts-categories/tools-thumb.webp.asset.json";
import toolsHero from "@/assets/parts-categories/tools-hero.webp.asset.json";
import towingCard from "@/assets/parts-categories/towing-card.webp.asset.json";
import towingThumb from "@/assets/parts-categories/towing-thumb.webp.asset.json";
import towingHero from "@/assets/parts-categories/towing-hero.webp.asset.json";

import batteryCard from "@/assets/parts-categories/battery-card.webp.asset.json";
import batteryThumb from "@/assets/parts-categories/battery-thumb.webp.asset.json";
import batteryHero from "@/assets/parts-categories/battery-hero.webp.asset.json";
import cargoCard from "@/assets/parts-categories/cargo-card.webp.asset.json";
import cargoThumb from "@/assets/parts-categories/cargo-thumb.webp.asset.json";
import cargoHero from "@/assets/parts-categories/cargo-hero.webp.asset.json";
import cleaningCard from "@/assets/parts-categories/cleaning-card.webp.asset.json";
import cleaningThumb from "@/assets/parts-categories/cleaning-thumb.webp.asset.json";
import cleaningHero from "@/assets/parts-categories/cleaning-hero.webp.asset.json";
import maintenanceCard from "@/assets/parts-categories/maintenance-card.webp.asset.json";
import maintenanceThumb from "@/assets/parts-categories/maintenance-thumb.webp.asset.json";
import maintenanceHero from "@/assets/parts-categories/maintenance-hero.webp.asset.json";
import wheelsCard from "@/assets/parts-categories/wheels-card.webp.asset.json";
import wheelsThumb from "@/assets/parts-categories/wheels-thumb.webp.asset.json";
import wheelsHero from "@/assets/parts-categories/wheels-hero.webp.asset.json";
import publicationsCard from "@/assets/parts-categories/publications-card.webp.asset.json";
import publicationsThumb from "@/assets/parts-categories/publications-thumb.webp.asset.json";
import publicationsHero from "@/assets/parts-categories/publications-hero.webp.asset.json";
import campsiteCard from "@/assets/parts-categories/campsite-card.webp.asset.json";
import campsiteThumb from "@/assets/parts-categories/campsite-thumb.webp.asset.json";
import campsiteHero from "@/assets/parts-categories/campsite-hero.webp.asset.json";
import tiedownsCard from "@/assets/parts-categories/tiedowns-card.webp.asset.json";
import tiedownsThumb from "@/assets/parts-categories/tiedowns-thumb.webp.asset.json";
import tiedownsHero from "@/assets/parts-categories/tiedowns-hero.webp.asset.json";

export type CategoryImageSize = "thumb" | "card" | "hero";

type Variant = Record<CategoryImageSize, string>;

export const CATEGORY_IMAGES: Record<string, Variant> = {
  accessories: { thumb: accessoriesThumb.url, card: accessoriesCard.url, hero: accessoriesHero.url },
  appliances:  { thumb: appliancesThumb.url,  card: appliancesCard.url,  hero: appliancesHero.url  },
  electrical:  { thumb: electricalThumb.url,  card: electricalCard.url,  hero: electricalHero.url  },
  engine:      { thumb: engineThumb.url,      card: engineCard.url,      hero: engineHero.url      },
  exterior:    { thumb: exteriorThumb.url,    card: exteriorCard.url,    hero: exteriorHero.url    },
  hardware:    { thumb: hardwareThumb.url,    card: hardwareCard.url,    hero: hardwareHero.url    },
  hvac:        { thumb: hvacThumb.url,        card: hvacCard.url,        hero: hvacHero.url        },
  interior:    { thumb: interiorThumb.url,    card: interiorCard.url,    hero: interiorHero.url    },
  lighting:    { thumb: lightingThumb.url,    card: lightingCard.url,    hero: lightingHero.url    },
  lpgas:       { thumb: lpgasThumb.url,       card: lpgasCard.url,       hero: lpgasHero.url       },
  marine:      { thumb: marineThumb.url,      card: marineCard.url,      hero: marineHero.url      },
  plumbing:    { thumb: plumbingThumb.url,    card: plumbingCard.url,    hero: plumbingHero.url    },
  suspension:  { thumb: suspensionThumb.url,  card: suspensionCard.url,  hero: suspensionHero.url  },
  tools:       { thumb: toolsThumb.url,       card: toolsCard.url,       hero: toolsHero.url       },
  towing:      { thumb: towingThumb.url,      card: towingCard.url,      hero: towingHero.url      },
  battery: { thumb: batteryThumb.url, card: batteryCard.url, hero: batteryHero.url },
  cargo: { thumb: cargoThumb.url, card: cargoCard.url, hero: cargoHero.url },
  cleaning: { thumb: cleaningThumb.url, card: cleaningCard.url, hero: cleaningHero.url },
  maintenance: { thumb: maintenanceThumb.url, card: maintenanceCard.url, hero: maintenanceHero.url },
  wheels: { thumb: wheelsThumb.url, card: wheelsCard.url, hero: wheelsHero.url },
  publications: { thumb: publicationsThumb.url, card: publicationsCard.url, hero: publicationsHero.url },
  campsite: { thumb: campsiteThumb.url, card: campsiteCard.url, hero: campsiteHero.url },
  tiedowns: { thumb: tiedownsThumb.url, card: tiedownsCard.url, hero: tiedownsHero.url },
};

/**
 * Resolve a category to a CDN-hosted WebP variant. Accepts either:
 *   - a known image key (e.g. "accessories")
 *   - the legacy DB `image_url` (we extract the basename, e.g.
 *     ".../parts-categories/accessories.jpg" -> "accessories")
 *   - the basic_category display name (matched via the alias table below)
 *
 * Falls back to the original `image_url` (or undefined) when no match exists,
 * so admin uploads outside this set still work.
 */
const CATEGORY_ALIAS: Record<string, string> = {
  "accessories & misc": "accessories",
  "appliances": "appliances",
  "electrical": "electrical",
  "engine / drivetrain": "engine",
  "exterior": "exterior",
  "hardware & fasteners": "hardware",
  "hvac": "hvac",
  "interior": "interior",
  "lighting": "lighting",
  "lp gas": "lpgas",
  "marine": "marine",
  "plumbing": "plumbing",
  "suspension & steering": "suspension",
  "tools & maintenance": "tools",
  "towing & trailer": "towing",
  "batteries & power": "battery",
  "truck bed & tonneau": "cargo",
  "batteries boxes & accessory rv": "battery",
  "battery box": "battery",
  "battery hold down": "battery",
  "battery tray": "battery",
  "bump stops": "suspension",
  "leaf spring support": "suspension",
  "trailer stabilization": "suspension",
  "campsite accessories rv": "campsite",
  "steps & step rugs rv": "campsite",
  "cargo & storage": "cargo",
  "cargo carrier": "cargo",
  "truck camper products": "cargo",
  "cleaning & detailing": "cleaning",
  "display-accessories": "accessories",
  "exterior hardware rv": "exterior",
  "hardware": "hardware",
  "hitch accessories": "towing",
  "hitch class iv & v": "towing",
  "hitches": "towing",
  "receiver hitch accessories": "towing",
  "weight distributing hitch": "towing",
  "weight distributing hitch acce": "towing",
  "tow wiring": "electrical",
  "towing": "towing",
  "rv camper tiedowns & turnbuckl": "tiedowns",
  "tie downs": "tiedowns",
  "tie-down straps & anchors rv": "tiedowns",
  "lp rv": "lpgas",
  "maintenance": "maintenance",
  "winterizing accessories rv": "maintenance",
  "publications": "publications",
  "tools": "tools",
  "wheels & brakes": "wheels",
};

function keyFromImageUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  const m = url.match(/parts-categories\/([^/?#.]+)/i);
  return m && m[1] ? m[1].toLowerCase() : undefined;
}

export function categoryImage(
  category: { image_url?: string | null; basic_category?: string | null } | string | null | undefined,
  size: CategoryImageSize = "card",
): string | undefined {
  if (!category) return undefined;
  if (typeof category === "string") {
    const k = CATEGORY_ALIAS[category.toLowerCase()] || category.toLowerCase();
    return CATEGORY_IMAGES[k]?.[size];
  }
  const fromUrl = keyFromImageUrl(category.image_url);
  if (fromUrl && CATEGORY_IMAGES[fromUrl]) return CATEGORY_IMAGES[fromUrl][size];
  const fromName = category.basic_category
    ? CATEGORY_ALIAS[category.basic_category.toLowerCase()]
    : undefined;
  if (fromName && CATEGORY_IMAGES[fromName]) return CATEGORY_IMAGES[fromName][size];
  return category.image_url ?? undefined;
}